import { Laboratory, Product } from '@/types/product';
import { NewsItem } from '@/types/news';
import { collection, getDocs, addDoc, deleteDoc, doc, getDoc, updateDoc, query, orderBy } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage, COLLECTIONS } from '@/lib/firebase';

export interface DataService {
  // Laboratories
  getLaboratories(): Promise<Laboratory[]>;
  addLaboratory(laboratory: Omit<Laboratory, 'id'>): Promise<Laboratory>;
  deleteLaboratory(id: string): Promise<void>;
  
  // Products
  getProducts(): Promise<Product[]>;
  addProduct(product: Omit<Product, 'id'>): Promise<Product>;
  deleteProduct(id: string): Promise<void>;

  // News
  getNews(): Promise<NewsItem[]>;
  getNewsById(id: string): Promise<NewsItem | null>;
  addNews(news: Omit<NewsItem, 'id'>): Promise<NewsItem>;
  updateNews(id: string, news: Partial<NewsItem>): Promise<NewsItem>;
  deleteNews(id: string): Promise<void>;
}

// Firebase Implementation
export class FirebaseDataService implements DataService {
  async getLaboratories(): Promise<Laboratory[]> {
    const querySnapshot = await getDocs(collection(db, COLLECTIONS.LABORATORIES));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Laboratory[];
  }

  async addLaboratory(laboratory: Omit<Laboratory, 'id'>): Promise<Laboratory> {
    const docRef = await addDoc(collection(db, COLLECTIONS.LABORATORIES), {
      ...laboratory,
      createdAt: new Date().toISOString()
    });
    return {
      id: docRef.id,
      ...laboratory
    };
  }

  async deleteLaboratory(id: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTIONS.LABORATORIES, id));
  }

  async getProducts(): Promise<Product[]> {
    const querySnapshot = await getDocs(collection(db, COLLECTIONS.PRODUCTS));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as Product[];
  }

  async addProduct(product: Omit<Product, 'id'>): Promise<Product> {
    const docRef = await addDoc(collection(db, COLLECTIONS.PRODUCTS), {
      ...product,
      createdAt: new Date().toISOString()
    });
    return {
      id: docRef.id,
      ...product
    };
  }

  async deleteProduct(id: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTIONS.PRODUCTS, id));
  }

  async getNews(): Promise<NewsItem[]> {
    const q = query(collection(db, COLLECTIONS.NEWS), orderBy('date', 'desc'));
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as NewsItem[];
  }

  async getNewsById(id: string): Promise<NewsItem | null> {
    const docRef = doc(db, COLLECTIONS.NEWS, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as NewsItem;
    }
    return null;
  }

  async addNews(news: Omit<NewsItem, 'id'>): Promise<NewsItem> {
    const docRef = await addDoc(collection(db, COLLECTIONS.NEWS), {
      ...news,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return {
      id: docRef.id,
      ...news
    };
  }

  async updateNews(id: string, news: Partial<NewsItem>): Promise<NewsItem> {
    const docRef = doc(db, COLLECTIONS.NEWS, id);
    const updateData = {
      ...news,
      updatedAt: new Date().toISOString()
    };
    
    await updateDoc(docRef, updateData);
    
    const updatedDoc = await getDoc(docRef);
    return {
      id: updatedDoc.id,
      ...updatedDoc.data()
    } as NewsItem;
  }

  async deleteNews(id: string): Promise<void> {
    await deleteDoc(doc(db, COLLECTIONS.NEWS, id));
  }
}

// Export the Firebase service as the default implementation
export const dataService = new FirebaseDataService(); 